import { aiErrorMessage } from "@rpforms/shared/build/actions/ai";
import {
    generateMailText,
    IMailTextMessage,
    IMailTextRequest,
} from "@rpforms/shared/build/actions/mailText";
import { PrimaryButton } from "@rpforms/shared/build/components/layout/Button";
import React, { useEffect, useRef, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { clipboardWrite } from "../chatgpt/clipboard";

interface IModalMailText {
    entry: any;
    show: boolean;
    onClose: () => void;
}

const MAIL_TEXT_SLUGS = ["000_angebot", "000_rechnung"];

export const supportsMailText = (entry: any): boolean => {
    const identifier = String(entry?.form?.slug ?? entry?.form?.name ?? "").toLowerCase();
    return MAIL_TEXT_SLUGS.includes(identifier);
};

export const buildRefineMessages = (
    history: IMailTextMessage[],
    currentText: string
): IMailTextMessage[] => {
    const messages = [...history];
    for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === "assistant") {
            messages[i] = { ...messages[i], content: currentText };
            return messages;
        }
    }
    return [...messages, { role: "assistant", content: currentText }];
};

export const mailTextErrorMessage = (err: any): string =>
    aiErrorMessage(err, "Mailtext konnte nicht erstellt werden");

export const ModalMailText = ({ entry, show, onClose }: IModalMailText) => {
    const [text, setText] = useState("");
    const [history, setHistory] = useState<IMailTextMessage[]>([]);
    const [instruction, setInstruction] = useState("");
    const [loading, setLoading] = useState(false);
    const [hasResult, setHasResult] = useState(false);
    const requestSeq = useRef(0);

    const request = async (payload: IMailTextRequest) => {
        const seq = ++requestSeq.current;
        setLoading(true);
        try {
            const result = await generateMailText(entry.id, payload);
            if (seq !== requestSeq.current) {
                return;
            }
            setText(result.content);
            setHistory(result.messages);
            setHasResult(true);
            setInstruction("");
        } catch (err) {
            if (seq === requestSeq.current) {
                toast.error(mailTextErrorMessage(err));
            }
        } finally {
            if (seq === requestSeq.current) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        if (!show) {
            requestSeq.current += 1;
            return;
        }
        setText("");
        setHistory([]);
        setInstruction("");
        setHasResult(false);
        request({ messages: [] });
    }, [show]);

    const handleRefine = () => {
        if (!instruction.trim() || loading) {
            return;
        }
        const messages = buildRefineMessages(history, text);
        request({ messages, instruction });
    };

    const handleCopy = async () => {
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                clipboardWrite(text);
            }
            toast.success("Mailtext kopiert", { autoClose: 2000 });
        } catch (err) {
            clipboardWrite(text);
            toast.success("Mailtext kopiert", { autoClose: 2000 });
        }
    };

    return (
        <Modal show={show} onHide={onClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>KI-Mailtext</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading && !hasResult ? (
                    <div className="text-center" style={{ padding: "40px 0" }}>
                        <Spinner animation="border" />
                        <p className="text-muted" style={{ marginTop: "12px" }}>
                            Mailtext wird erstellt…
                        </p>
                    </div>
                ) : (
                    <>
                        <textarea
                            className="form-control"
                            rows={14}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            disabled={loading}
                        />
                        <div className="form-group" style={{ marginTop: "14px" }}>
                            <label htmlFor="mail-text-instruction">Korrektur / Stichpunkte</label>
                            <input
                                id="mail-text-instruction"
                                type="text"
                                className="form-control"
                                placeholder="z. B. auf Wartung von letztem Freitag eingehen"
                                value={instruction}
                                onChange={(e) => setInstruction(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        handleRefine();
                                    }
                                }}
                                disabled={loading}
                            />
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-secondary" onClick={onClose}>
                    Schließen
                </button>
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleRefine}
                    disabled={loading || !instruction.trim()}
                >
                    Neu formulieren
                </button>
                <PrimaryButton
                    type="button"
                    onClick={handleCopy}
                    disabled={loading || !text.trim()}
                >
                    Kopieren
                </PrimaryButton>
            </Modal.Footer>
        </Modal>
    );
};
